import {translate} from "react-i18nify";

import * as actionTypes from "../constants/actionTypes";
import * as formulaTypes from "../constants/formulaTypes";
import * as reducerHelper from "../helpers/reducerHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as formatHelper from "../helpers/formatHelper";

const initialState = {
    active: false,
    product: null,
    fandeck: null,
    color: null,
    formula: null,
    base: null,
    bipp: null,
    package: null,
    margin: null,
    customer: null,
    buildingSite: null,
    packageList: [],
    priceList: [],
    productFileList: [],
    numberOfLabels: 1,
    numCanDone: 0,
    numCanTotal: 1,
    dispensing: false,
    dispenseNote: null,
    moreInfoInternal: null,
    printData: null,
    tmcData: null,
    infoData: null,
    moreInfoText: null,
    moreInfoData: null,
    statisticsId: null,
    activeUnit: null,
    history: null,
    alternative: null,
    validation: null,
    colorantList: null,
    customPackageAmounts: null,
    spectralData: null,
    buildingSiteList: null,
    readOnly: false
};

/**
 * Structure of object from formula store:
 * - product {Object}
 * - fandeck {Object}
 * - color {Object}
 * - formula {Object}
 * - base {Object}
 * - bipp {Object}
 * - package {Object}       - selected package object
 * - packageList {Array}    - array of available package objects for formula
 * - priceList {Array}
 * - productFileList {Array}
 * - margin {Object}
 * - customer {Object}
 * - buildingSite {String}
 * - numCanDone {Number}    - number of dispensed packages
 * - numCanTotal {Number}   - number of packages
 * - dispensing {Boolean}
 * - dispenseNote {String}
 * - moreInfoInternal {String}
 * - printData {Object}
 * - tmcData {Object}
 * - infoData {Object}
 * - moreInfoText {String}
 * - moreInfoData {Object}
 * - statisticsId {Number}
 * - activeUnit {String}
 * - history {Array | null}
 * - alternative {Array | null}
 * - validation {Object | null}
 * - colorantList {Array | null}
 * - customPackageAmounts {Object | null}
 * - spectralData {Object | null}
 * - buildingSiteList {Array | null}
 * - readOnly {Boolean}
 */
export const formulaReducer = (state = {}, action) => {
    switch (action.type) {
        case actionTypes.FAVORITES_TAB_INIT:
        case actionTypes.FORMULA_STORE_INIT:
            if (state[action.payload.tabId] !== undefined) {
                return state;
            }

            let newInitialState = {};
            newInitialState[action.payload.tabId] = initialState;

            return {...state, ...newInitialState};
        case actionTypes.SEARCH_PANEL_INIT:
            let newFormulaInitialState = {};

            if (state[action.payload.tabId] !== undefined) {
                newFormulaInitialState[action.payload.tabId] = {
                    ...initialState,
                    activeUnit: state[action.payload.tabId].activeUnit
                };
            } else {
                newFormulaInitialState[action.payload.tabId] = initialState;
            }

            return {...state, ...newFormulaInitialState};
        case actionTypes.CUSTOM_TAB_INIT:
            if (state[action.payload.tabId] !== undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    formula: modelHelper.mapCustomFormulaToObject(),
                    margin: action.payload.allMarginList.length > 0 ? action.payload.allMarginList[0] : null
                },
                state
            );
        case actionTypes.MATCHING_TAB_INIT:
            if (state[action.payload.tabId] !== undefined) {
                return state;
            }

            return reducerHelper.getNewState(action.payload.tabId, {...initialState}, state);
        case actionTypes.LOAD_FORMULA_DATA_FROM_STATS:
        case actionTypes.LOAD_FORMULA_META_DATA_FROM_STATS:
            if (state[action.payload.tabId] === undefined) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        ...initialState,
                        statisticsId: action.payload.colorStatsId
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    statisticsId: action.payload.colorStatsId,
                    activeUnit: state[action.payload.tabId].activeUnit
                },
                state
            );
        case actionTypes.SAVE_FORMULA_MORE_INFO:
            return reducerHelper.getNewState(action.payload.tabId, {moreInfoText: action.payload.moreInfo}, state);
        case actionTypes.SAVE_FORMULA_SPECTRAL_DATA:
            if (
                state[action.payload.tabId] === undefined ||
                state[action.payload.tabId].fandeck === null ||
                state[action.payload.tabId].color === null
            ) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    spectralData: action.payload.spectralData
                },
                state
            );
        case actionTypes.SAVE_FORMULA_DATA_FROM_STATS:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: action.payload.active && action.payload.formula !== null,
                    product: action.payload.product,
                    fandeck: action.payload.fandeck,
                    color: action.payload.color,
                    formula: action.payload.formula,
                    customer: action.payload.customer,
                    buildingSite: action.payload.buildingSite,
                    base: action.payload.base,
                    bipp: action.payload.bipp,
                    package: action.payload.package,
                    packageList: action.payload.packageList,
                    margin: action.payload.margin,
                    priceList: action.payload.priceList,
                    productFileList: action.payload.productFileList,
                    numCanDone: action.payload.numCanDone,
                    numCanTotal: action.payload.numCanTotal,
                    dispenseNote: action.payload.dispenseNote,
                    moreInfoInternal: null,
                    printData: action.payload.printData,
                    tmcData: action.payload.tmcData,
                    infoData: action.payload.infoData,
                    moreInfoText: action.payload.moreInfoText,
                    moreInfoData: action.payload.moreInfoData,
                    history: action.payload.history,
                    alternative: action.payload.alternative,
                    validation: action.payload.validation,
                    colorantList: null,
                    customPackageAmounts: null,
                    spectralData: action.payload.spectralData
                },
                state
            );
        case actionTypes.SAVE_FORMULA_STATISTICS_ID:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (action.payload.colorStatsId === null && state[action.payload.tabId].formula !== null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formula: state[action.payload.tabId].formula.updateStatsType(null),
                        statisticsId: action.payload.colorStatsId
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    statisticsId: action.payload.colorStatsId
                },
                state
            );
        case actionTypes.SAVE_FORMULA_DATA:
            let imageId = -1;
            if (state[action.payload.tabId] && state[action.payload.tabId].package !== null) {
                imageId = state[action.payload.tabId].package.package.imageId;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: action.payload.formula !== null,
                    formula: action.payload.formula,
                    base: action.payload.base,
                    bipp: action.payload.bipp,
                    package: action.payload.package === null ? null : action.payload.package.updateImageId(imageId),
                    packageList: action.payload.packageList,
                    margin: action.payload.margin,
                    priceList: [],
                    productFileList: [],
                    moreInfoText: action.payload.moreInfoText,
                    moreInfoData: action.payload.moreInfoData,
                    statisticsId: null,
                    history: null,
                    alternative: null,
                    validation: action.payload.validation,
                    colorantList: null,
                    customPackageAmounts: null,
                    spectralData: null
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_PACKAGE_IMAGE:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].package === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    package: state[action.payload.tabId].package.updateImageId(action.payload.imageId)
                },
                state
            );
        case actionTypes.SAVE_FORMULA_META_AND_PRINT_DATA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            let formulaValidation = action.payload.validation;
            if (state[action.payload.tabId].validation !== null) {
                formulaValidation = state[action.payload.tabId].validation.updateValidationErrors(
                    action.payload.validation.validationErrors
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    priceList: modelHelper.mapFormulaPricesToObjects(action.payload.priceList),
                    printData: modelHelper.formatPrintData(action.payload.printData),
                    tmcData: action.payload.tmcData,
                    infoData: action.payload.infoData,
                    productFileList: modelHelper.mapFilesToObjects(action.payload.productFiles),
                    validation: formulaValidation,
                    customPackageAmounts: action.payload.customPackageAmounts
                },
                state
            );
        case actionTypes.CLONE_FORMULA_DATA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (action.payload.formula === null) {
                return reducerHelper.getNewState(action.payload.newTabId, {...state[action.payload.tabId]}, state);
            }

            let validation = state[action.payload.tabId].validation;
            if (action.payload.formula.isManualCorrection() && validation !== null) {
                validation = state[action.payload.tabId].validation.reset();
            }

            return reducerHelper.getNewState(
                action.payload.newTabId,
                {
                    ...state[action.payload.tabId],
                    formula: action.payload.formula,
                    numCanDone: action.payload.formula.isCorrection() ? 1 : state[action.payload.tabId].numCanDone,
                    numCanTotal:
                        action.payload.packageCount === null
                            ? state[action.payload.tabId].numCanTotal
                            : action.payload.packageCount,
                    validation: validation,
                    readOnly: true
                },
                state
            );
        case actionTypes.CLONE_FORMULA_DATA_AND_RESET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.newTabId,
                {
                    ...state[action.payload.tabId],
                    active:
                        state[action.payload.tabId].formula !== null &&
                        state[action.payload.tabId].formula.isAvailable(),
                    formula:
                        action.payload.formula === null ? state[action.payload.tabId].formula : action.payload.formula,
                    numCanDone: 0,
                    numCanTotal:
                        action.payload.packageCount === null
                            ? state[action.payload.tabId].numCanTotal
                            : action.payload.packageCount,
                    statisticsId: null,
                    readOnly: false
                },
                state
            );
        case actionTypes.REMOVE_FORMULA_DATA:
            let newTabList = Object.keys(state).filter((tabId) => Number(tabId) !== action.payload.tabId);
            let finalState = {};

            for (let tab of newTabList) {
                if (state[tab] !== undefined) {
                    finalState[tab] = state[tab];
                }
            }

            return finalState;
        case actionTypes.SELECT_PRODUCT:
            if (action.payload.bipp !== null && action.payload.bipp.product.id !== action.payload.product.id) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        active: false,
                        product: action.payload.product,
                        bipp: null,
                        formula: null,
                        package: null,
                        history: null,
                        alternative: null,
                        statisticsId: null,
                        colorantList: null
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: false,
                    product: action.payload.product,
                    formula: null,
                    package: null,
                    history: null,
                    alternative: null,
                    statisticsId: null
                },
                state
            );
        case actionTypes.AUTOSELECT_PRODUCT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    product: action.payload.product
                },
                state
            );
        case actionTypes.SELECT_FANDECK:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: false,
                    fandeck: action.payload.fandeck,
                    formula: null,
                    package: null,
                    history: null,
                    alternative: null,
                    statisticsId: null
                },
                state
            );
        case actionTypes.AUTOSELECT_FANDECK:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeck: action.payload.fandeck
                },
                state
            );
        case actionTypes.SELECT_FORMULA:
        case actionTypes.SELECT_FORMULA_NAME_AND_CODE:
        case actionTypes.SELECT_COLOR_PALETTE_FORMULA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: false,
                    color: action.payload.color,
                    formula: null,
                    package: null,
                    history: null,
                    alternative: null,
                    statisticsId: null
                },
                state
            );
        case actionTypes.AUTOSELECT_FORMULA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    color: action.payload.color
                },
                state
            );
        case actionTypes.SAVE_COLOR_IN_FANDECK_RGB:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].color === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    color: state[action.payload.tabId].color.updateCodeAndRgb(action.payload.code, action.payload.rgb)
                },
                state
            );
        case actionTypes.SAVE_FORMULA_CIF_RGB:
            if (
                state[action.payload.tabId] === undefined ||
                state[action.payload.tabId].color === null ||
                state[action.payload.tabId].formula === null
            ) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    color: state[action.payload.tabId].color.updateCodeAndRgb(action.payload.code, action.payload.rgb),
                    formula: state[action.payload.tabId].formula.updateCodeAndRgb(
                        action.payload.code,
                        action.payload.rgb
                    )
                },
                state
            );
        case actionTypes.SELECT_BASE:
            if (action.payload.bipp !== null && action.payload.bipp.base.id !== action.payload.base.id) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        active: false,
                        base: action.payload.base,
                        bipp: null,
                        formula: null,
                        package: null,
                        history: null,
                        alternative: null,
                        statisticsId: null,
                        colorantList: null
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: false,
                    base: action.payload.base,
                    formula: null,
                    package: null,
                    history: null,
                    alternative: null,
                    statisticsId: null,
                    colorantList: null
                },
                state
            );
        case actionTypes.AUTOSELECT_BASE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    base: action.payload.base
                },
                state
            );
        case actionTypes.SELECT_BIPP:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: false,
                    product: action.payload.bipp.product,
                    base: action.payload.bipp.base,
                    bipp: action.payload.bipp,
                    formula: null,
                    package: null,
                    history: null,
                    alternative: null,
                    statisticsId: null,
                    colorantList: null
                },
                state
            );
        case actionTypes.AUTOSELECT_BIPP:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    product: action.payload.bipp.product,
                    base: action.payload.bipp.base,
                    bipp: action.payload.bipp
                },
                state
            );
        case actionTypes.MAIN_TAB_SET_PACKAGE:
            if (action.payload.selectedPackage.package.isCustom()) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        package: action.payload.selectedPackage
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    bipp: action.payload.selectedPackage,
                    package: action.payload.selectedPackage
                },
                state
            );
        case actionTypes.CUSTOM_TAB_SET_PACKAGE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    package: action.payload.selectedPackage
                },
                state
            );
        case actionTypes.SEARCH_PANEL_REMOVE_SELECTED:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    product: state[action.payload.tabId].product,
                    fandeck: state[action.payload.tabId].fandeck,
                    color: state[action.payload.tabId].color,
                    base: state[action.payload.tabId].base,
                    bipp: state[action.payload.tabId].bipp,
                    margin: state[action.payload.tabId].margin,
                    customer: state[action.payload.tabId].customer,
                    buildingSite: null,
                    dispensing: state[action.payload.tabId].dispensing,
                    dispenseNote: state[action.payload.tabId].dispenseNote,
                    moreInfoInternal: state[action.payload.tabId].moreInfoInternal,
                    activeUnit: state[action.payload.tabId].activeUnit
                },
                state
            );
        case actionTypes.FAV_TAB_REMOVE_FROM_FAVORITES:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active: false,
                    product: null,
                    fandeck: null,
                    color: null,
                    formula: null,
                    base: null,
                    bipp: null,
                    package: null,
                    packagesList: []
                },
                state
            );
        case actionTypes.SET_SEARCH_PANEL:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    product: action.payload.product,
                    fandeck: action.payload.fandeck,
                    color: action.payload.color,
                    base: action.payload.base,
                    bipp: null,
                    activeUnit: state[action.payload.tabId] ? state[action.payload.tabId].activeUnit : null
                },
                state
            );
        case actionTypes.UPDATE_SEARCH_PANEL:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    product: action.payload.product,
                    fandeck: action.payload.fandeck,
                    color: action.payload.color,
                    base: action.payload.base,
                    bipp: action.payload.bipp
                },
                state
            );
        case actionTypes.SAVE_BROWSE_COLOR_LIST:
            if (
                state[action.payload.tabId] === undefined ||
                state[action.payload.tabId].color === null ||
                action.payload.data.some((color) => color.id === state[action.payload.tabId].color.id)
            ) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    color: null
                },
                state
            );
        case actionTypes.SELECT_FROM_FAVORITES:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    product: action.payload.product,
                    fandeck: action.payload.fandeck,
                    color: action.payload.color,
                    base: action.payload.base,
                    bipp: null,
                    activeUnit: state[action.payload.tabId].activeUnit
                },
                state
            );
        case actionTypes.NAVIGATION_SAVE_FORMULA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    product: action.payload.product,
                    fandeck: action.payload.fandeck,
                    color: action.payload.color,
                    base: action.payload.base,
                    bipp: action.payload.bipp
                },
                state
            );
        case actionTypes.NAVIGATION_SAVE_QUICK_TINT_FORMULA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    formula: action.payload.formula,
                    margin: action.payload.margin
                },
                state
            );
        case actionTypes.FORMULA_COLORANTS_EDIT:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            if (action.payload.modified) {
                let editedFormula = state[action.payload.tabId].formula.editColorantList(
                    action.payload.colorantList,
                    action.payload.unit,
                    action.payload.shotsCoef,
                    action.payload.shotsName
                );

                if (action.payload.statsType !== null) {
                    editedFormula.statsType = action.payload.statsType;
                }

                if (
                    [formulaTypes.OWN_FORMULA_SOURCE.MATCHING, null].includes(
                        state[action.payload.tabId].formula.formulaSourceId
                    )
                ) {
                    editedFormula.formulaSourceId = formulaTypes.OWN_FORMULA_SOURCE.USER_CREATED;
                }

                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formula: editedFormula,
                        package:
                            action.payload.customPackage === null
                                ? state[action.payload.tabId].package
                                : action.payload.customPackage
                    },
                    state
                );
            }

            if (action.payload.scaling !== 1 || state[action.payload.tabId].formula.scaling !== 1) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formula: state[action.payload.tabId].formula.scaleFormula(action.payload.scaling),
                        package:
                            action.payload.customPackage === null
                                ? state[action.payload.tabId].package
                                : action.payload.customPackage
                    },
                    state
                );
            }

            if (action.payload.customPackage === null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formula: state[action.payload.tabId].formula.resetModifications(
                            state[action.payload.tabId].color
                        )
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.resetModifications(state[action.payload.tabId].color),
                    package: action.payload.customPackage
                },
                state
            );
        case actionTypes.UPDATE_FORMULA_DISPENSING_INFO:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (action.payload.numCanDone !== null && action.payload.numCanTotal !== null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        numCanDone: action.payload.numCanDone,
                        numCanTotal: action.payload.numCanTotal,
                        dispensing: action.payload.dispensing,
                        readOnly: action.payload.dispensing || action.payload.numCanDone > 0
                    },
                    state
                );
            }

            if (action.payload.numCanDone !== null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        numCanDone: action.payload.numCanDone,
                        dispensing: action.payload.dispensing,
                        readOnly: action.payload.dispensing || action.payload.numCanDone > 0
                    },
                    state
                );
            }

            if (action.payload.numCanTotal !== null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        numCanTotal: action.payload.numCanTotal,
                        dispensing: action.payload.dispensing,
                        readOnly: action.payload.dispensing || state[action.payload.tabId].numCanDone > 0
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    dispensing: action.payload.dispensing,
                    readOnly: action.payload.dispensing || state[action.payload.tabId].numCanDone > 0
                },
                state
            );
        case actionTypes.SET_NUMBER_OF_PACKAGES:
        case actionTypes.STATISTICS_TAB_SET_NUMBER_OF_PACKAGES:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    numCanTotal: action.payload.numberOfPackages
                },
                state
            );
        case actionTypes.CUSTOM_TAB_SELECT_PRODUCT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    product: action.payload.product,
                    base: null,
                    package: null,
                    packageList: []
                },
                state
            );
        case actionTypes.CUSTOM_TAB_AUTOSELECT_FANDECK:
        case actionTypes.CUSTOM_TAB_SELECT_FANDECK:
        case actionTypes.MATCHING_SELECT_CUSTOM_FANDECK:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeck: action.payload.fandeck
                },
                state
            );
        case actionTypes.CUSTOM_TAB_SAVE_PACKAGE_LIST:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    package: action.payload.selectedPackage,
                    packageList: action.payload.packageList
                },
                state
            );
        case actionTypes.CUSTOM_TAB_SELECT_BASE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    base: action.payload.base,
                    colorantList: null
                },
                state
            );
        case actionTypes.SAVE_CUSTOM_TAB_DATA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    ...initialState,
                    product: action.payload.product,
                    color: action.payload.color,
                    fandeck: action.payload.fandeck,
                    formula: action.payload.formula,
                    base: action.payload.base,
                    bipp: null,
                    package: action.payload.package,
                    packageList: action.payload.packageList,
                    margin: action.payload.margin,
                    activeUnit: action.payload.activeUnit,
                    spectralData: action.payload.spectralData,
                    moreInfoText: action.payload.moreInfoText,
                    readOnly: true
                },
                state
            );
        case actionTypes.MAIN_TAB_SET_MARGIN:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    margin: action.payload.margin
                },
                state
            );
        case actionTypes.MACHINE_SAVE_VALIDATION_RESULT:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            let validationUpdate = action.payload.validation;
            if (state[action.payload.tabId].validation !== null) {
                validationUpdate = state[action.payload.tabId].validation.updateMachineValidationAll(
                    action.payload.validation.machineValidationAll
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.validateColorants(action.payload.result),
                    validation: validationUpdate
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_FORMULA_COLORANTS:
        case actionTypes.MAIN_TAB_SAVE_STANDARD_FORMULA_COLORANTS:
            if (
                state[action.payload.tabId] === undefined ||
                state[action.payload.tabId].formula === null ||
                state[action.payload.tabId].package === null
            ) {
                return state;
            }

            if (state[action.payload.tabId].package.isCustom()) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        formula: state[action.payload.tabId].formula.updateColorantAmounts(
                            action.payload.colorantListMl,
                            action.payload.colorantListG,
                            action.payload.colorantListShot
                        ),
                        package: state[action.payload.tabId].package.updatePackageFillingMl(action.payload.fillingMl)
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.updateColorantAmounts(
                        action.payload.colorantListMl,
                        action.payload.colorantListG,
                        action.payload.colorantListShot
                    )
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_MODIFIED_FORMULA_COLORANTS:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.updateModifiedColorantAmounts(
                        action.payload.colorantListMl,
                        action.payload.colorantListG,
                        action.payload.colorantListShot
                    )
                },
                state
            );
        case actionTypes.CUSTOM_TAB_CHANGE_FORMULA_NAME:
        case actionTypes.MATCHING_TAB_CHANGE_COLOR_NAME:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.updateName(action.payload.name)
                },
                state
            );
        case actionTypes.CUSTOM_TAB_CHANGE_FORMULA_CODE:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.updateCode(action.payload.code)
                },
                state
            );
        case actionTypes.CUSTOM_TAB_SET_MORE_INFO_TEXT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    moreInfoText: action.payload.moreInfoText
                },
                state
            );
        case actionTypes.FORMULA_TAB_EDIT_FORMULA_RGB:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.updateRgb(action.payload.rgb)
                },
                state
            );
        case actionTypes.CUSTOM_TAB_REMOVE_SELECTED_PRODUCT:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    product: null,
                    base: null,
                    package: null,
                    packageList: [],
                    priceList: [],
                    margin: null
                },
                state
            );
        case actionTypes.CUSTOM_TAB_REMOVE_SELECTED_FANDECK:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeck: null,
                    priceList: [],
                    margin: null
                },
                state
            );
        case actionTypes.CUSTOM_TAB_REMOVE_SELECTED_BASE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    base: null,
                    package: null,
                    packageList: [],
                    priceList: [],
                    margin: null
                },
                state
            );
        case actionTypes.SAVE_CUSTOMER_DETAIL:
            if (state[action.payload.tabId] && state[action.payload.tabId].active) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        customer: modelHelper.mapCustomerToObject(action.payload.customer),
                        buildingSite: null,
                        buildingSiteList: null
                    },
                    state
                );
            }
            return state;
        case actionTypes.MAIN_TAB_SET_CUSTOMER:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    customer: action.payload.customer,
                    buildingSite: null,
                    buildingSiteList: null
                },
                state
            );
        case actionTypes.MAIN_TAB_DELETE_CUSTOMER:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    customer: null,
                    buildingSite: null,
                    buildingSiteList: null
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_DISPENSE_NOTE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    dispenseNote: action.payload.note
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_INTERNAL_NOTE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    moreInfoInternal: action.payload.note
                },
                state
            );
        case actionTypes.MAIN_TAB_DELETE_DISPENSE_NOTE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    dispenseNote: null
                },
                state
            );
        case actionTypes.MAIN_TAB_DELETE_INTERNAL_NOTE:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    moreInfoInternal: null
                },
                state
            );
        case actionTypes.SET_NUMBER_OF_LABELS:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    numberOfLabels: action.payload.numberOfLabels
                },
                state
            );
        case actionTypes.SAVE_HISTORICAL_FORMULAS:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    history: action.payload.formulaList
                },
                state
            );
        case actionTypes.SAVE_ALTERNATIVE_FORMULAS:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    alternative: action.payload.formulaList
                },
                state
            );
        case actionTypes.UPDATE_FORMULA_FROM_HISTORICAL_FORMULA:
            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: action.payload.formulaData.formula,
                    base: action.payload.formulaData.base,
                    bipp: action.payload.formulaData.bipp,
                    package: action.payload.formulaData.bipp,
                    moreInfoText: action.payload.formulaData.moreInfoText,
                    moreInfoData: action.payload.formulaData.moreInfoData,
                    packageList: action.payload.formulaData.bippList
                },
                state
            );
        case actionTypes.SAVE_MANUAL_FORMULA:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].formula === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: state[action.payload.tabId].formula.updateColorantListMachine(
                        modelHelper.mapManualColorantsToObjects(
                            state[action.payload.tabId].formula.realColorantList,
                            action.payload.formula
                        )
                    )
                },
                state
            );
        case actionTypes.SAVE_FANDECK_COLOR_COUNT:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].fandeck === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeck: state[action.payload.tabId].fandeck.updateColorCount(action.payload.colorCount)
                },
                state
            );
        case actionTypes.SELECT_CLOSEST_COLOR_AND_FANDECK:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    product: action.payload.product,
                    fandeck: action.payload.fandeck,
                    color: action.payload.color,
                    dispenseNote:
                        translate("spectroTab.tolerance") +
                        ": " +
                        formatHelper.formatDecimalNumber(action.payload.delta)
                },
                state
            );
        case actionTypes.RCC_WEBSOCKET_CLOSED:
            let finished = true;

            for (let index in state) {
                if (state[index].numCanDone === state[index].numCanTotal) {
                    finished = false;
                }
            }

            if (finished) {
                return state;
            }

            let closedState = {};

            for (let index in state) {
                if (state[index].numCanDone === state[index].numCanTotal) {
                    closedState[index] = {
                        ...state[index],
                        numCanDone: 0,
                        statisticsId: null,
                        readOnly: false
                    };
                } else {
                    closedState[index] = state[index];
                }
            }

            return closedState;
        case actionTypes.SETTINGS_SAVE_VALIDATE_COLORANT_BASE_RULES:
            let colorantBaseRulesState = {};

            for (let index in state) {
                if (state[index].validation === null) {
                    colorantBaseRulesState[index] = state[index];
                } else {
                    colorantBaseRulesState[index] = {
                        ...state[index],
                        validation: state[index].validation.updateColorantBaseRules(action.payload.value)
                    };
                }
            }

            return colorantBaseRulesState;
        case actionTypes.SETTINGS_SAVE_VALIDATE_TOTAL_CNT_AMOUNTS:
            let totalCntAmountsState = {};

            for (let index in state) {
                if (state[index].validation === null) {
                    totalCntAmountsState[index] = state[index];
                } else {
                    totalCntAmountsState[index] = {
                        ...state[index],
                        validation: state[index].validation.updateTotalCntAmounts(action.payload.value)
                    };
                }
            }

            return totalCntAmountsState;
        case actionTypes.SETTINGS_SAVE_VALIDATE_OVERFILL:
            let overfillState = {};

            for (let index in state) {
                if (state[index].validation === null) {
                    overfillState[index] = state[index];
                } else {
                    overfillState[index] = {
                        ...state[index],
                        validation: state[index].validation.updateOverfill(action.payload.value)
                    };
                }
            }

            return overfillState;
        case actionTypes.SETTINGS_SAVE_VALIDATE_MIN_DOSABLE_AMOUNT:
            let minDosableAmountState = {};

            for (let index in state) {
                if (state[index].validation === null) {
                    minDosableAmountState[index] = state[index];
                } else {
                    minDosableAmountState[index] = {
                        ...state[index],
                        validation: state[index].validation.updateMinDosableAmount(action.payload.value)
                    };
                }
            }

            return minDosableAmountState;
        case actionTypes.SETTINGS_SAVE_COLORANT_BATCH_FOR_MULTIPLE_CANS:
            let availableCntAmountsState = {};

            for (let index in state) {
                if (state[index].validation === null) {
                    availableCntAmountsState[index] = state[index];
                } else {
                    availableCntAmountsState[index] = {
                        ...state[index],
                        validation: state[index].validation.updateAvailableColorantAmounts(action.payload.value)
                    };
                }
            }

            return availableCntAmountsState;
        case actionTypes.SET_COLORANT_PANEL_ACTIVE_UNIT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeUnit: action.payload.activeUnit
                },
                state
            );
        case actionTypes.SET_COLORANT_PANEL_COLORANT_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    colorantList: action.payload.colorantList
                },
                state
            );
        case actionTypes.UPDATE_PRODUCT_FILE_PAGES:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productFileList: state[action.payload.tabId].productFileList.map((productFile) => {
                        if (productFile.id === action.payload.fileId) {
                            return productFile.updateNumberOfPages(action.payload.numberOfPages);
                        }

                        return productFile;
                    })
                },
                state
            );
        case actionTypes.UPDATE_PRODUCT_FILE_PAGES_ONLINE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    productFileList: state[action.payload.tabId].productFileList.map((productFile) => {
                        if (productFile.id === action.payload.fileId) {
                            return productFile.updateNumberOfPagesOnline(action.payload.numberOfPages);
                        }

                        return productFile;
                    })
                },
                state
            );
        case actionTypes.SET_CLOSEST_COLORS_FOR_MISSING_FORMULA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    history: action.payload.colorList
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_BUILDING_SITES:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    buildingSiteList: action.payload.buildingSiteList
                },
                state
            );
        case actionTypes.MAIN_TAB_SAVE_BUILDING_SITES_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined || state[action.payload.tabId].buildingSiteList === null) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    buildingSiteList: state[action.payload.tabId].buildingSiteList.concat(
                        action.payload.buildingSiteList
                    )
                },
                state
            );
        case actionTypes.MAIN_TAB_SET_BUILDING_SITE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            if (action.payload.buildingSite === null) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        buildingSite: null
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    buildingSite: action.payload.buildingSite.trim().length === 0 ? null : action.payload.buildingSite
                },
                state
            );
        case actionTypes.MAIN_TAB_DELETE_BUILDING_SITE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    buildingSite: null
                },
                state
            );
        case actionTypes.MAIN_TAB_RELOAD_REQUIRED:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    readOnly: true
                },
                state
            );
        case actionTypes.MAIN_TAB_RESET_PRICE_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    priceList: []
                },
                state
            );
        case actionTypes.MATCHING_TAB_INIT_DRAFT_FORMULA_SCENE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    active:
                        state[action.payload.tabId].product !== null &&
                        state[action.payload.tabId].fandeck !== null &&
                        state[action.payload.tabId].formula !== null &&
                        state[action.payload.tabId].base !== null &&
                        state[action.payload.tabId].package !== null,
                    readOnly: true
                },
                state
            );
        case actionTypes.MATCHING_TAB_SET_CORRECTION_SCENE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    formula: action.payload.targetFormula.updateFormulaBeforeCorrection()
                },
                state
            );
        case actionTypes.SAVE_FORMULA_VALIDATION:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    validation: action.payload.validation
                },
                state
            );
        case actionTypes.CLOSE_ACTIVE_TAB:
        case actionTypes.CLOSE_ALL_TABS_EXCEPT_ACTIVE:
        case actionTypes.CLOSE_ALL_TABS:
            let finalStateClose = {};

            for (let tab of action.payload.newTabList) {
                if (state[tab.id] !== undefined) {
                    finalStateClose[tab.id] = state[tab.id];
                }
            }

            return finalStateClose;
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return {};
        default:
            return state;
    }
};
